


























































import { PropType } from 'vue';

import Validatable from '@/mixins/validatable';
import BindsAttrs from '@/mixins/binds-attrs';
import mixins from '@/utils/mixins';

import { mapActions } from 'vuex';
import { get } from 'vuex-pathify';

import { deepEqual, getPropertyFromItem } from '@/utils/helpers';

import { Module } from '@/models/configurator/module';
import { PropValidator } from 'vue/types/options';
import { QuoteProductItem } from '@/models/quote';
import { Configuration } from '@/models/configurator/configuration';
import { IQuoteProductItem } from '@/models';

const baseMixins = mixins(BindsAttrs, Validatable);

interface Options extends InstanceType<typeof baseMixins> {
  /* eslint-disable-next-line camelcase */
  $_modelEvent: string;
}

export type InputValidationRule = (value: any) => string | boolean;

export default baseMixins
  .extend<Options>({
    functional: false
  })
  .extend({
    name: 'QuoteWizardModuleSelector',

    props: {
      id: String,
      value: (null as any) as PropType<any>,
      hideDetails: [Boolean, String] as PropType<boolean | 'auto'>,
      hint: String,
      label: String,
      persistentHint: Boolean,
      valueComparator: {
        type: Function,
        default: deepEqual
      } as PropValidator<typeof deepEqual>,
      modules: Array as PropType<number[]>
    },

    inheritAttrs: false,

    data() {
      return {
        lazyValue: this.value,
        hasMouseDown: false,
        searchModuleQuery: '',
        productItems: [] as IQuoteProductItem[]
      };
    },

    computed: {
      modulesList: get<Module[]>('configuratorModule/modules'),

      getModuleById: get<(id: number) => Module>(
        'configuratorModule/getModuleById'
      ),

      computedId(): string {
        return this.id || `input-${(this as any)._uid}`;
      },
      hasDetails(): boolean {
        return this.messagesToDisplay.length > 0;
      },
      hasHint(): boolean {
        return (
          !this.hasMessages &&
          !!this.hint &&
          (this.persistentHint || this.isFocused)
        );
      },
      hasLabel(): boolean {
        return !!(this.$slots.label || this.label);
      },
      internalValue: {
        get(): any {
          return this.lazyValue;
        },
        set(val: any) {
          this.lazyValue = val;
          this.$emit(this.$_modelEvent, val);
        }
      },
      isDirty(): boolean {
        return !!this.lazyValue;
      },
      isLabelActive(): boolean {
        return this.isDirty;
      },
      messagesToDisplay(): string[] {
        if (this.hasHint) return [this.hint];

        if (!this.hasMessages) return [];

        return this.validations
          .map((validation: string | InputValidationRule) => {
            if (typeof validation === 'string') return validation;

            const validationResult = validation(this.internalValue);

            return typeof validationResult === 'string' ? validationResult : '';
          })
          .filter(message => message !== '');
      },
      showDetails(): boolean {
        return (
          this.hideDetails === false ||
          (this.hideDetails === 'auto' && this.hasDetails)
        );
      },

      selectedModules(): Module[] {
        return this.modulesList.filter(item =>
          this.productItems.map(product => product.module).includes(item.id)
        );
      }
    },

    watch: {
      value(val) {
        this.lazyValue = val;
      },
      modules(val: number[]) {
        this.productItems = val.map(module => {
          return Object.assign({}, new QuoteProductItem({ module }));
        });

        this.internalValue = this.productItems;
      }
    },

    beforeCreate() {
      // v-radio-group needs to emit a different event
      // https://github.com/vuetifyjs/vuetify/issues/4752
      this.$_modelEvent =
        (this.$options.model && this.$options.model.event) || 'input';
    },

    methods: {
      ...mapActions({
        updateConfiguration: 'configuratorConfiguration/updateConfiguration',
        createConfiguration: 'configuratorConfiguration/createConfiguration',
        fetchConfiguration: 'configuratorConfiguration/fetchConfiguration'
      }),

      findExistingIndex(item: object) {
        const itemValue = this.getValue(item);

        return (this.internalValue || []).findIndex((i: object) =>
          this.valueComparator(this.getValue(i), itemValue)
        );
      },

      getText(item: object) {
        return getPropertyFromItem(item, 'name', item);
      },
      getValue(item: object) {
        return getPropertyFromItem(item, 'id', this.getText(item));
      },

      setValue(value: any) {
        const oldValue = this.internalValue;
        this.internalValue = value;
        value !== oldValue && this.$emit('change', value);
      },

      isConfigured(item: QuoteProductItem) {
        const index = this.productItems.indexOf(item);
        return this.internalValue && this.internalValue[index].configuration;
      },

      selectItem(item: Module) {
        // add multiple check
        const internalValue = (this.internalValue || []).slice();
        const i = this.findExistingIndex(item);
        i !== -1 ? internalValue.splice(i, 1) : internalValue.push(item);
        this.setValue(
          internalValue.map((i: object) => {
            // return this.returnObject ? i : this.getValue(i)
            return this.getValue(i);
          })
        );
      },

      openConfiguration(item: any) {
        const index: number = this.internalValue.indexOf(item);
        const configurationFormDialog = (this as any).$refs[
          `ConfigurationFormDialog`
        ];

        const openDialog = async (isConfigured: boolean) => {
          if (isConfigured) {
            return this.updateConfiguration().then(
              (configuration: Configuration) => configuration
            );
          } else {
            // return undefined on if no configuration is set
            return Promise.resolve(void 0);
            // return this.createConfiguration().then(
            //   (configuration: Configuration) => configuration
            // );
          }
        };

        openDialog(this.isConfigured(item)).then(() => {
          async (configuration?: Configuration) => {
            await configurationFormDialog
              .open(configuration)
              .then((result: Configuration) => {
                this.internalValue[index].configuration = result.id;
                this.internalValue[index].price = result.price;
              });
          };
        });
      }
    }
  });
